// src/components/DevToolsLauncher.tsx

'use client'

import React, { useState, useEffect } from 'react'
import { DevTools } from './DevTools'
import { useDevTools } from '../hooks/useDevTools'
import { Monitor, Settings2 } from 'lucide-react'

// Browser-safe production check
function isProduction(): boolean {
  if (typeof window === 'undefined') {
    return true // SSR safe - assume production
  }

  // Check NODE_ENV pokud je dostupné
  if (typeof globalThis !== 'undefined') {
    const globalProcess = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process
    if (globalProcess?.env?.NODE_ENV) {
      return globalProcess.env.NODE_ENV === 'production'
    }
  }

  // Fallback - check por development indicators
  const port = window.location.port
  const hostname = window.location.hostname
  const devPorts = ['3000', '3001', '5173', '8080', '4200', '8000', '9000']
  const devHosts = ['localhost', '127.0.0.1', '0.0.0.0']

  // If on dev host/port, assume development
  const isDev = devHosts.includes(hostname) || devPorts.includes(port)

  return !isDev // Return true for production if NOT on dev host/port
}

export function DevToolsTrigger() {
  const { isOpen, toggle } = useDevTools()
  const [showTooltip, setShowTooltip] = useState(false)
  const [shouldHide, setShouldHide] = useState(false)

  // Skrýt pokud je no-dev-tools parametr
  useEffect(() => {
    const checkParams = () => {
      const urlParams = new URLSearchParams(window.location.search)
      setShouldHide(urlParams.get('no-dev-tools') === 'true')
    }

    checkParams()
    window.addEventListener('popstate', checkParams)

    return () => window.removeEventListener('popstate', checkParams)
  }, [])

  // Zobrazit pouze v development mode
  if (isProduction()) {
    return null
  }

  if (shouldHide) {
    return null
  }

  const triggerStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 999997,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }

  const buttonStyles: React.CSSProperties = {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: isOpen
      ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
      : 'linear-gradient(135deg, #ffffff, #f8fafc)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: isOpen
      ? '0 20px 25px -5px rgba(59, 130, 246, 0.4), 0 10px 10px -5px rgba(59, 130, 246, 0.2)'
      : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isOpen ? 'scale(1.1)' : 'scale(1)',
    color: isOpen ? '#ffffff' : '#374151',
    outline: 'none',
  }

  const tooltipStyles: React.CSSProperties = {
    position: 'absolute',
    bottom: '100%',
    right: '0',
    marginBottom: '12px',
    padding: '12px 16px',
    background: 'rgba(17, 24, 39, 0.95)',
    color: '#ffffff',
    fontSize: '12px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    opacity: showTooltip ? 1 : 0,
    transform: showTooltip ? 'translateY(0)' : 'translateY(4px)',
    transition: 'all 0.2s ease-out',
    pointerEvents: 'none',
    zIndex: 999998,
  }

  return (
    <>
      <div style={triggerStyles}>
        <button
          onClick={toggle}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          style={buttonStyles}
          title="Open DevTools"
        >
          {isOpen ? (
            <Settings2 size={24} style={{ animation: 'spin 2s linear infinite' }} />
          ) : (
            <Monitor size={24} />
          )}
        </button>

        <div style={tooltipStyles}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>DevTools</div>
          <div style={{ opacity: 0.8, fontSize: '11px' }}>
            {isOpen ? 'Click to close' : 'Ctrl+Shift+D'}
          </div>
        </div>

        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              width: '12px',
              height: '12px',
              background: '#10b981',
              borderRadius: '50%',
              border: '2px solid #ffffff',
              animation: 'pulse 2s ease-in-out infinite',
            }}
          />
        )}
      </div>

      <DevTools isOpen={isOpen} onToggle={toggle} />

      <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
    </>
  )
}

// Quick integration component
export function DevToolsLauncher({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <DevToolsTrigger />
    </>
  )
}

// HOC pro snadnou integraci
export function withDevTools<T extends object>(Component: React.ComponentType<T>) {
  return function DevToolsWrapper(props: T) {
    return (
      <DevToolsLauncher>
        <Component {...props} />
      </DevToolsLauncher>
    )
  }
}

// Context pro pokročilé použití
interface DevToolsContextType {
  isOpen: boolean
  toggle: () => void
  open: () => void
  close: () => void
}

const DevToolsContext = React.createContext<DevToolsContextType | undefined>(undefined)

export function DevToolsProvider({ children }: { children: React.ReactNode }) {
  const devTools = useDevTools()

  return (
    <DevToolsContext.Provider value={devTools}>
      {children}
      <DevTools isOpen={devTools.isOpen} onToggle={devTools.toggle} />
    </DevToolsContext.Provider>
  )
}

export function useDevToolsContext() {
  const context = React.useContext(DevToolsContext)
  if (context === undefined) {
    throw new Error('useDevToolsContext must be used within a DevToolsProvider')
  }
  return context
}

// Utility pro testování konkrétních komponent
export function DevToolsStandalone({ url }: { url?: string }) {
  const [isOpen, setIsOpen] = useState(true)

  if (isProduction()) {
    return null
  }

  return <DevTools url={url} isOpen={isOpen} onToggle={() => setIsOpen(!isOpen)} />
}

// Re-export hook
export { useDevTools } from '../hooks/useDevTools'
